import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  addCoreApplication,
  coreApplicationsPageQuery,
  coreApplicationsTableQuery,
  removeCoreApplications,
  saveDashboardElements,
} from '../api/proactivitee'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { SelectPicker } from '../ui/SelectPicker'
import { coreAppColumns } from './snmpColumns'
import { CoreAppRow } from './CoreAppRow'

/** `core_aplications` e typo-ul portalului si ramane cheia salvata. */
const ELEMENT_OPTIONS = [
  { value: 'core_aplications', label: 'Core Applications' },
  { value: 'virtual_infrastructure', label: 'Virtual Infrastructure' },
  { value: 'physical_servers', label: 'Servers' },
  { value: 'data_storage', label: 'Data Storage' },
  { value: 'network_infrastructure', label: 'Network Infrastructure' },
  { value: 'development_infrastructure', label: 'Development Infrastructure' },
]

/**
 * Core Applications - `Proactivitee::core_applications`, la
 * /app/proactivitee/core_applications.
 *
 * ---------------------------------------------------------------------------
 * „APARE UN TEXT INAINTE DE TABEL"
 * ---------------------------------------------------------------------------
 *
 * Textul ala era ETICHETA unui camp care nu se mai vedea, si atat.
 *
 * Macheta veche (`core_applications/index.php`) punea deasupra tabelului un
 * `<select class="form-control selectpicker" multiple>` cu cele sase grupuri
 * ale tabloului de bord, si eticheta „Dashboard Elements" langa el. In portal,
 * bootstrap-select 1.12 ridica select-ul intr-un buton cu meniu. Aici nu
 * ruleaza niciun script de-al portalului, dar FOAIA lui se incarca - iar in
 * `public/css/app.css` (13550) scrie:
 *
 *     select.bs-select-hidden,
 *     select.selectpicker { display: none !important; }
 *
 * Deci select-ul era stins de foaie, butonul care trebuia sa-i ia locul nu-l
 * mai desena nimeni, si din tot campul ramanea pe ecran numai eticheta -
 * masurata in Edge la 204x27px, cu 42px deasupra tabelului. Un cuvant orfan.
 *
 * NU se sterge: ar fi insemnat sa scoatem singurul loc din care se alege ce
 * arata Applications Dashboard, si omul ar fi ramas cu un tablou pe care nu-l
 * mai poate schimba de nicaieri. Se pune la loc CAMPUL, cu `SelectPicker` -
 * acela deseneaza singur markupul lui bootstrap-select, fara jQuery - si urca
 * in bara de sus, langa cautare, unde `list_chrome.css` are deja masura pentru
 * alegerea proprie a unui ecran (`tk-header-select`).
 *
 * ---------------------------------------------------------------------------
 * DE-ACOLO VENEA SI TABELUL STRICAT
 * ---------------------------------------------------------------------------
 *
 * Campul statea intr-un `<div class="row">` pus de-a dreptul in pagina. Un
 * `.row` Bootstrap are `margin: 0 -15px` si se bizuie pe cei 15px de padding ai
 * invelisului. In portal ii avea: `#page-content` vine cu `padding: 20px`. In
 * v4 ecranele de lista sting padding-ul ala dinadins
 * (`body.tk-list-flush #page-content { padding: 0 }`, list_chrome.css 486), ca
 * lista sa inceapa lipita de meniu - si atunci randul iese 15px in afara
 * ferestrei pe fiecare latura. Masurat in Edge la 1600px: `.row` lat de 1630px
 * pornind de la x=-15, documentul intins la 1615px, si o bara de derulare
 * orizontala peste tot ecranul.
 *
 * `list_chrome.css` stie de povestea asta, dar numai pentru randurile pe care
 * le face DataTables in jurul tabelului (`.dataTables_wrapper > .row`). Un
 * `.row` scris de pagina nu e prins de nicio regula.
 *
 * Pagina mai punea si un al doilea `<div id="content">` inauntrul lui
 * `Content`, care e chiar `<div id="content">`: doua elemente cu acelasi id.
 */
export function CoreApplicationsPage() {
  const page = useQuery(coreApplicationsPageQuery())
  const [state, setState] = useTableState('coreApplications', { start: 0, length: 25, order: { column: 1, dir: 'asc' }, search: '' })
  const columns = useMemo(() => coreAppColumns(), [])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[1]
  const table = useQuery({
    ...coreApplicationsTableQuery({
      draw: 1, start: state.start, length: state.length,
      orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const [addOpen, setAddOpen] = useState(false)
  const [name, setName] = useState('')

  /* Alegerea vine de pe server o data si o tine pagina dupa aceea: butonul
     trebuie sa arate pe loc ce a apasat omul, nu abia cand se intoarce
     salvarea. */
  const [elements, setElements] = useState<string[]>([])
  useEffect(() => {
    if (!page.data) return
    const saved = (page.data.elements ?? {}) as Record<string, number>
    setElements(ELEMENT_OPTIONS.map((o) => o.value).filter((key) => Boolean(saved[key])))
  }, [page.data])

  function picked(): number[] {
    return Array.from(document.querySelectorAll<HTMLInputElement>('#coreApplications .oneInput:checked')).map((el) => Number(el.value))
  }

  return (
    <Content>
      <DataTable
        id="coreApplications"
        columnTools
        columns={columns}
        rows={table.data?.data ?? []}
        /* Cu o cautare in casuta, insigna trebuie sa numere ce s-a gasit, nu
           tot ce exista - altfel numarul si paginarea spun lucruri diferite. */
        total={table.data?.recordsFiltered ?? table.data?.recordsTotal ?? 0}
        fetching={table.isFetching}
        error={table.isError ? (table.error as Error).message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(r) => String(r.DT_RowId)}
        emptyHint="No core applications yet. Add one, then hang under it the machines that serve it."
        details={(row) => (
          <CoreAppRow
            applicationId={Number(row.application_id)}
            devices={(page.data?.devices ?? []) as Record<string, unknown>[]}
          />
        )}
        headerView={(
          <div
            className="tk-lift-to-header tk-header-select"
            style={{ display: 'inline-block', textAlign: 'left' }}
            title="Which groups Applications Dashboard shows"
          >
            <SelectPicker
              id="dashboardElements"
              name="dashboard_elements[]"
              title="Dashboard Elements"
              multiple
              selectedTextFormat="count > 0"
              size={6}
              options={ELEMENT_OPTIONS}
              value={elements}
              onChange={(next) => {
                setElements(next)
                void saveDashboardElements(next)
              }}
            />
          </div>
        )}
        headerButtons={(
          <>
            <a className="btn btn-primary add-application tk-header-extra" href="#add" onClick={(e) => { e.preventDefault(); setAddOpen(true) }}>
              <i className="fas fa-plus" /> Add application
            </a>
            <a className="btn btn-danger remove-applications tk-header-extra" href="#remove" onClick={(e) => {
              e.preventDefault()
              const ids = picked()
              if (ids.length) void removeCoreApplications(ids).then(() => table.refetch())
            }}>
              <i className="fas fa-times-circle" /> Remove
            </a>
          </>
        )}
      />

      {addOpen ? (
        <div className="modal fade in" id="addApplicationModal" tabIndex={-1} role="dialog" style={{ display: 'block' }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button type="button" className="close" aria-label="Close" onClick={() => setAddOpen(false)}><span aria-hidden="true">&times;</span></button>
                <h4 className="modal-title" id="myModalLabel">Add application</h4>
              </div>
              <div className="modal-body">
                <form id="addApplicationForm" className="form-horizontal" onSubmit={(e) => e.preventDefault()}>
                  <div className="form-group">
                    <label className="col-sm-3"><span className="color">Application name</span></label>
                    <div className="col-sm-9">
                      <input type="text" className="form-control" name="application_name" value={name} required onChange={(e) => setName(e.target.value)} />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-default" onClick={() => setAddOpen(false)}>Close</button>
                <button type="button" className="btn btn-primary addApplication" onClick={() => {
                  void addCoreApplication(name).then(() => { setAddOpen(false); setName(''); void table.refetch() })
                }}>Save</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Content>
  )
}
